import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, TextField,
    CircularProgress, Alert, InputAdornment, Chip,
} from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    ThumbUp as YesIcon,
    ThumbDown as NoIcon,
    QuestionMark as MaybeIcon,
    Place as PlaceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { publicEvents } from '../services/api';

const VOTE_CFG = {
    YES:   { label: 'Works for me',  color: '#16A34A', bg: '#DCFCE7', border: '#86EFAC', icon: <YesIcon /> },
    MAYBE: { label: 'Might work',    color: '#B45309', bg: '#FEF9C3', border: '#FCD34D', icon: <MaybeIcon /> },
    NO:    { label: "Can't make it", color: '#DC2626', bg: '#FEE2E2', border: '#FCA5A5', icon: <NoIcon /> },
};

const PublicVote = () => {
    const { token }  = useParams();
    const navigate   = useNavigate();

    const [event,         setEvent]         = useState(null);
    const [error,         setError]         = useState('');
    const [loading,       setLoading]       = useState(true);
    const [guestName,     setGuestName]     = useState('');
    const [guestEmail,    setGuestEmail]    = useState('');
    const [nameSubmitted, setNameSubmitted] = useState(false);
    const [votes,         setVotes]         = useState({});
    const [submitted,     setSubmitted]     = useState(false);
    const [submitting,    setSubmitting]    = useState(false);

    useEffect(() => {
        publicEvents.getByToken(token)
            .then(res => setEvent(res.data))
            .catch(() => setError('This event link is invalid or has expired.'))
            .finally(() => setLoading(false));
    }, [token]);

    const handleStartVoting = () => {
        if (!guestName.trim() || !guestEmail.trim()) return;
        setNameSubmitted(true);
    };

    const handleVote = (slotId, status) => {
        setVotes(prev => ({ ...prev, [slotId]: prev[slotId] === status ? undefined : status }));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const validVotes = Object.entries(votes).filter(([, v]) => v);
            await Promise.all(
                validVotes.map(([slotId, status]) =>
                    publicEvents.castGuestVote(token, Number(slotId), guestName.trim(), guestEmail.trim() || null, status)
                )
            );
            const existing = JSON.parse(localStorage.getItem('pendingEventTokens') || '[]');
            if (!existing.includes(token)) {
                localStorage.setItem('pendingEventTokens', JSON.stringify([...existing, token]));
            }
            setSubmitted(true);
        } catch {
            setError('Failed to submit votes. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── States ────────────────────────────────────────────────────────────────
    if (loading) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <CircularProgress sx={{ color:'#7C3AED' }} />
        </Box>
    );

    if (error && !event) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center', px:3 }}>
                <Box sx={{ width:72, height:72, borderRadius:'50%', background:'linear-gradient(135deg,#EDE9FE,#C7D2FE)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3 }}>
                    <CalendarIcon sx={{ fontSize:32, color:'#7C3AED' }} />
                </Box>
                <Typography variant="h5" fontWeight={800} gutterBottom>Link not found</Typography>
                <Typography color="text.secondary">{error}</Typography>
            </Box>
        </Box>
    );

    if (submitted) return (
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', bgcolor:'#F8FAFC' }}>
            <Box sx={{ textAlign:'center', maxWidth:420, px:3 }}>
                <Box sx={{ width:88, height:88, borderRadius:'50%', background:'linear-gradient(135deg,#7C3AED,#4F46E5)', display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:3, boxShadow:'0 12px 32px rgba(124,58,237,0.3)' }}>
                    <Typography sx={{ fontSize:40 }}>🗓️</Typography>
                </Box>
                <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color:'#0F172A' }}>Votes recorded!</Typography>
                <Typography color="text.secondary" sx={{ mb:4, lineHeight:1.7 }}>
                    Thanks, <strong>{guestName}</strong>! The organizer will pick the best time based on everyone's availability.
                </Typography>
                <Box sx={{ display:'flex', gap:1.5, justifyContent:'center' }}>
                    <Button variant="contained" onClick={() => navigate('/register')}
                        sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)' }}>
                        Create free account
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/login')}
                        sx={{ fontWeight:700, borderRadius:2.5, borderColor:'#7C3AED', color:'#7C3AED', '&:hover':{ bgcolor:'#EDE9FE' } }}>
                        Log in
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    // ── Main view ─────────────────────────────────────────────────────────────
    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC' }}>

            {/* Header */}
            <Box sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', py:5, position:'relative', overflow:'clip' }}>
                <Box sx={{ position:'absolute', top:-40, left:'-3%', width:280, height:280, borderRadius:'50%', background:'rgba(255,255,255,0.07)', pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', bottom:-30, right:'5%', width:200, height:200, borderRadius:'50%', background:'rgba(255,255,255,0.05)', pointerEvents:'none' }} />
                <Box sx={{ position:'relative', zIndex:1, display:'flex', flexDirection:'column', alignItems:'center', textAlign:'center', gap:1.5, px:3 }}>
                    <Box sx={{ width:56, height:56, borderRadius:3, bgcolor:'rgba(255,255,255,0.18)', border:'2px solid rgba(255,255,255,0.3)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <CalendarIcon sx={{ fontSize:28, color:'white' }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={800} sx={{ color:'white', lineHeight:1.2 }}>
                            {event?.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color:'rgba(255,255,255,0.85)', mt:0.5 }}>
                            {nameSubmitted
                                ? `Hi ${guestName} — pick the times that work for you`
                                : "You've been invited to vote on a meeting time"}
                        </Typography>
                    </Box>
                    <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', justifyContent:'center' }}>
                        {event?.location && (
                            <Box sx={{ display:'flex', alignItems:'center', gap:0.5, px:2, py:0.5, borderRadius:99, bgcolor:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)' }}>
                                <PlaceIcon sx={{ fontSize:13, color:'rgba(255,255,255,0.9)' }} />
                                <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.9)', fontWeight:600 }}>{event.location}</Typography>
                            </Box>
                        )}
                        <Box sx={{ px:2, py:0.5, borderRadius:99, bgcolor:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.9)', fontWeight:600 }}>
                                {event?.timeSlots?.length} time option{event?.timeSlots?.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ maxWidth:600, mx:'auto', px:3, py:5, display:'flex', flexDirection:'column', gap:3 }}>

                {event?.description && (
                    <Paper elevation={0} variant="outlined" sx={{ borderRadius:3, p:3, bgcolor:'#F5F3FF', borderColor:'#EDE9FE' }}>
                        <Typography variant="body2" sx={{ color:'#4C1D95', lineHeight:1.7 }}>{event.description}</Typography>
                    </Paper>
                )}

                {!nameSubmitted ? (
                    /* ── Enter name / email ── */
                    <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>
                        <Box sx={{ px:3, py:2.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)' }}>
                            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700 }}>Who's voting?</Typography>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color:'white' }}>Enter your details to get started</Typography>
                        </Box>
                        <Box sx={{ px:3, py:3, bgcolor:'white', display:'flex', flexDirection:'column', gap:2.5 }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Your name *</Typography>
                                <TextField fullWidth variant="outlined" required size="small"
                                    placeholder="What's your name?"
                                    value={guestName} onChange={e => setGuestName(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleStartVoting()}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.75, fontSize:'0.65rem' }}>Email *</Typography>
                                <TextField fullWidth variant="outlined" type="email" required size="small"
                                    placeholder="Your email address"
                                    value={guestEmail} onChange={e => setGuestEmail(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleStartVoting()}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize:17, color:'text.disabled' }} /></InputAdornment> }} />
                            </Box>
                            <Button variant="contained" size="large" disabled={!guestName.trim() || !guestEmail.trim()} onClick={handleStartVoting}
                                sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)', py:1.5 }}>
                                Continue to Vote
                            </Button>
                        </Box>
                    </Paper>
                ) : (
                    /* ── Vote on slots ── */
                    <>
                        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                            {event?.timeSlots?.map(slot => {
                                const myVote = votes[slot.id];
                                const isVoted = Boolean(myVote);
                                return (
                                    <Paper key={slot.id} elevation={0} variant="outlined" sx={{
                                        borderRadius:4, overflow:'hidden',
                                        borderColor: isVoted ? '#7C3AED' : 'rgba(0,0,0,0.1)',
                                        borderWidth: isVoted ? 2 : 1,
                                    }}>
                                        <Box sx={{
                                            px:3, py:2.5,
                                            background: isVoted
                                                ? 'linear-gradient(135deg,#7C3AED,#4F46E5)'
                                                : 'linear-gradient(135deg,#F8FAFC,#F1F5F9)',
                                            borderBottom:'1px solid', borderColor: isVoted ? 'transparent' : 'divider',
                                        }}>
                                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: isVoted ? 'white' : '#0F172A' }}>
                                                {format(new Date(slot.proposedStartTime), 'EEEE, MMMM d, yyyy')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: isVoted ? 'rgba(255,255,255,0.8)' : 'text.secondary', mt:0.25 }}>
                                                {format(new Date(slot.proposedStartTime), 'h:mm a')} – {format(new Date(slot.proposedEndTime), 'h:mm a')}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ px:3, py:2.5, bgcolor:'white', display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
                                            {Object.entries(VOTE_CFG).map(([status, cfg]) => {
                                                const isSelected = myVote === status;
                                                return (
                                                    <Box key={status} onClick={() => handleVote(slot.id, status)} sx={{
                                                        display:'flex', alignItems:'center', gap:0.75,
                                                        px:2, py:0.75, borderRadius:99, cursor:'pointer',
                                                        border:'1.5px solid',
                                                        borderColor: isSelected ? cfg.border : '#E5E7EB',
                                                        bgcolor: isSelected ? cfg.bg : 'transparent',
                                                        transition:'all 0.15s',
                                                        userSelect:'none',
                                                        '&:hover':{ borderColor: cfg.border, bgcolor: cfg.bg },
                                                    }}>
                                                        {React.cloneElement(cfg.icon, { sx:{ fontSize:14, color: isSelected ? cfg.color : '#94A3B8' } })}
                                                        <Typography variant="caption" fontWeight={700} sx={{ color: isSelected ? cfg.color : '#94A3B8' }}>
                                                            {cfg.label}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}

                                            {slot.votes?.length > 0 && (
                                                <Box sx={{ ml:'auto', display:'flex', gap:0.5, flexWrap:'wrap' }}>
                                                    {['YES','MAYBE','NO'].map(s => {
                                                        const count = slot.votes.filter(v => v.status === s).length;
                                                        if (!count) return null;
                                                        const cfg = VOTE_CFG[s];
                                                        return (
                                                            <Chip key={s} size="small"
                                                                label={`${count} ${s.toLowerCase()}`}
                                                                sx={{ fontSize:'0.65rem', height:20, bgcolor: cfg.bg, color: cfg.color, fontWeight:700, border:`1px solid ${cfg.border}` }} />
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>

                        {error && <Alert severity="error" sx={{ borderRadius:3 }}>{error}</Alert>}

                        <Button variant="contained" size="large"
                            disabled={Object.values(votes).filter(Boolean).length === 0 || submitting}
                            onClick={handleSubmit}
                            sx={{ fontWeight:700, borderRadius:2.5, py:1.75, fontSize:'1rem', background:'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'0 4px 14px rgba(124,58,237,0.3)', '&:hover':{ boxShadow:'0 6px 20px rgba(124,58,237,0.45)' } }}>
                            {submitting ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Submit My Votes'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign:'center' }}>
                            Vote yes, maybe, or no on each time option.
                        </Typography>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default PublicVote;
